import styled, { css } from 'styled-components';
import {
  TTypographyColor,
  TTypographyFontFamily,
  TTypographyLineHeight,
  TTypographySize,
} from './Typography';
import { medias } from '@/utils/sizes';
import { LoaderPseudoelementCSS } from '../BackgroundLoader/BackgroundLoader.styled';

export const Typography = styled.div.withConfig({
  shouldForwardProp: (prop) =>
    ![
      'size',
      'sizeMobile',
      'lineHeight',
      'hideDesktopBrInMobile',
      'fontFamily',
      'as',
      'color',
      'isLoader',
    ].includes(prop),
})<{
  size: TTypographySize;
  sizeMobile?: TTypographySize;
  color: TTypographyColor;
  lineHeight: TTypographyLineHeight;
  fontFamily: TTypographyFontFamily;
  hideDesktopBrInMobile: boolean;
  isLoader: boolean;
}>`
  ${({ as, size }) =>
    as &&
    css`
      --typography-fs: var(
        --fs-${size !== 'default' && size ? size : String(as)}
      );
    `}

  ${({ color }) =>
    color !== 'default' &&
    css`
      --typography-c: var(--c-font-${color});
    `}

  ${({ lineHeight }) =>
    lineHeight !== 'default' &&
    css`
      --typography-lh: var(--lh-${lineHeight});
    `}

  display: flex;
  flex-direction: column;
  font-size: var(--typography-fs, var(--fs-base));
  font-weight: 400 !important;
  color: var(--typography-c, var(--c-font-primary));
  line-height: var(--typography-lh, var(--lh-base));
  font-family: var(--typography-ff, var(--ff-helvetica-regular));
  padding: 0;
  margin: 0;

  ${({ fontFamily }) =>
    fontFamily !== 'default' &&
    css`
      --typography-ff: var(--ff-${fontFamily});
      font-weight: ${fontFamily === 'helvetica-light' ? 300 : 400} !important;
      letter-spacing: ${fontFamily === 'helvetica-light' ? '0.01em' : '0'};
    `}

  ${({ size }) =>
    size &&
    css`
      ${size === 'default' &&
      css`
        line-height: 1.4;
      `}
      ${size === 'base' &&
      css`
        line-height: 1.4;
      `}
    ${size === 'lg' &&
      css`
        line-height: 1.3;
      `}
    ${(size === 'xl' || size === '2xl' || size === '3xl') &&
      css`
        line-height: 1.2;
      `}
    ${(size === '4xl' || size === '5xl') &&
      css`
        line-height: 1.1;
      `}
    `}

  ${medias.max.mobile} {
    // TODO: add br only for mobile
    ${({ hideDesktopBrInMobile }) =>
      hideDesktopBrInMobile &&
      css`
        br {
          display: none;
        }

        br[data-mobile] {
          display: block;
        }
      `}

    ${({ size }) =>
      size &&
      css`
        ${size === 'lg' &&
        css`
          line-height: 1.4;
          font-family: var(--ff-helvetica-regular);
        `}

        ${(size === 'xl' || size === '2xl' || size === '3xl') &&
        css`
          line-height: 1.4;
          font-family: var(--ff-helvetica-regular);
        `}

      ${(size === '4xl' || size === '5xl') &&
        css`
          line-height: 1.2;
        `}
      `}

    ${({ sizeMobile }) =>
      sizeMobile &&
      css`
        --typography-fs: var(--fs-${sizeMobile});
        font-size: var(--typography-fs, var(--fs-base));
      `}
      ${({ sizeMobile }) =>
      (sizeMobile === '3xl' || sizeMobile === '4xl' || sizeMobile === '5xl') &&
      css`
        line-height: 1.2;
        font-family: var(--ff-helvetica-light);
      `}
  }

  ${({ isLoader }) =>
    isLoader &&
    css`
      position: relative;
      overflow: hidden;
      width: min-content;
      overflow: hidden;
      color: transparent;
      ${LoaderPseudoelementCSS}
    `}

  &[data-is-hidden='true'] {
    opacity: 0;
    pointer-events: none;
  }

  span {
    display: inline-block;
  }

  .animated-line-mobile {
    display: none;
  }

  .animated-line-mobile:has(div[data-maintain]),
  .animated-line:has(div[data-maintain]){
    display: inline-block;
  }

  div[data-maintain] {
    display: inline-block;
  }

  ${medias.max.mobile} {
    .animated-line-mobile {
      display: flex;
    }

    &:has(.animated-line) {
      display: none;
    }

    .animated-line {
      display: none;
    }
  }

  a {
    color: currentColor;
    display: contents;
  }

  &.has-indents {
    display: block;

    p {
    margin-block-start: 0;
    margin-block-end: 0;
    margin-inline-start: 0;
    margin-inline-end: 0;
    }
  }
`;
